import { SettingsTranslate } from './settingsTranslate.model';

const settingsTranslateEN: SettingsTranslate = {
  title: 'Settings',
  tabsLabel: 'Section',
  tabs: {
    studio: 'Studio',
    capture: 'Lead capture',
    templates: 'Templates',
    contracts: 'Contracts',
    account: 'Account',
    preferences: 'Preferences',
    followUp: 'Follow-up',
  },
  followUp: {
    title: 'Automatic follow-up',
    description:
      'Create tasks by itself when an event sits too long in a board stage.',
    empty: 'No rule created',
    created: 'Rule created',
    deleted: 'Rule removed',
    add: 'Add rule',
    variablesHint: 'Use {{leadVar}} and {{eventVar}} in the task title.',
    countingHint:
      'The countdown starts when the event enters the column. Editing the description, date or location does not reset it — only moving between columns does.',
    scheduleHint:
      'Rules are evaluated once a day, so nothing shows up instantly. The first run may create several tasks at once, for events that were already idle.',
    field: {
      status: 'Board stage',
      days: 'Days idle',
      title: 'Task title',
    },
    rule: {
      summary: 'Idle for {{days}} day(s) in {{status}}',
      active: 'Active',
      inactive: 'Inactive',
      remove: 'Remove rule',
    },
  },
  sections: {
    organization: {
      title: 'Organization',
    },
    theme: {
      title: 'Theme',
    },
    language: {
      title: 'Language',
    },
    security: {
      title: 'Security',
      changePassword: {
        label: 'Password',
        button: 'Change password',
      },
    },
    birthPhotographer: {
      title: 'Specialized Mode',
      label: 'Birth Photography Mode',
      description:
        'Enables specific fields for birth photographers. Data remains saved even when the mode is disabled.',
      error: 'Error updating setting',
    },
    leadCapture: {
      title: 'Lead capture form',
      label: 'Public lead capture form',
      description:
        'Generate a public link to capture new leads. Submitted contacts automatically show up in your client list.',
      copy: 'Copy link',
      tip: 'Tip: use this link in your Instagram bio.',
      disableConfirm: {
        title: 'Disable lead capture form',
        text: 'Are you sure you want to disable the lead capture form?',
        secondaryText: 'The current link will stop working.',
        confirm: 'Disable',
        cancel: 'Cancel',
      },
      toast: {
        enabled: 'Lead capture form enabled',
        disabled: 'Lead capture form disabled',
        error: 'Could not update the form. Please try again',
        linkCopied: 'Link copied',
        linkCopyError: 'Could not copy the link',
      },
    },
    messageTemplates: {
      title: 'Message templates',
    },
    checklists: {
      title: 'Event checklists',
    },
  },
  messageTemplates: {
    description: 'Create reusable messages with variables to send to clients.',
    add: 'New template',
    empty: 'No templates yet',
    actions: {
      edit: 'Edit template',
      delete: 'Delete template',
    },
    delete: {
      title: 'Delete template',
      text: 'Are you sure you want to delete "{{name}}"?',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    toast: {
      deleted: 'Template deleted',
      error: 'Could not delete the template',
    },
    modal: {
      titleCreate: 'New template',
      titleEdit: 'Edit template',
      fields: {
        name: 'Template name',
        body: 'Message',
      },
      errors: {
        name: {
          required: 'Name is required',
          maxLength: 'Name must be at most 120 characters',
        },
        body: {
          required: 'Message is required',
          maxLength: 'Message must be at most 2000 characters',
        },
      },
      variablesHint: 'Tap a variable to insert it into the message:',
      submit: 'Save',
      cancel: 'Cancel',
      toast: {
        created: 'Template created',
        updated: 'Template updated',
        error: 'Could not save the template',
      },
    },
  },
  checklists: {
    description:
      'Create checklists to apply to events with relative deadlines.',
    add: 'New checklist',
    empty: 'No checklists yet',
    itemsCount: '{{count}} item(s)',
    actions: {
      edit: 'Edit checklist',
      delete: 'Delete checklist',
    },
    delete: {
      title: 'Delete checklist',
      text: 'Are you sure you want to delete "{{name}}"?',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    toast: {
      deleted: 'Checklist deleted',
      error: 'Could not delete the checklist',
    },
    modal: {
      titleCreate: 'New checklist',
      titleEdit: 'Edit checklist',
      fields: {
        name: 'Checklist name',
      },
      items: {
        title: 'Items',
        add: 'Add item',
        itemTitle: 'Item title',
        offsetDays: 'Days',
        offsetHint:
          'Days relative to the event date (e.g. -7 = 7 days before, 0 = on the day).',
        remove: 'Remove item',
        empty: 'No items added',
      },
      errors: {
        name: {
          required: 'Name is required',
          maxLength: 'Name must be at most 120 characters',
        },
        items: {
          required: 'Add at least one item',
        },
        itemTitle: {
          required: 'Title is required',
        },
      },
      submit: 'Save',
      cancel: 'Cancel',
      toast: {
        created: 'Checklist created',
        updated: 'Checklist updated',
        error: 'Could not save the checklist',
      },
    },
  },
  organization: {
    name: {
      label: 'Organization name',
      placeholder: 'Organization name',
      submit: 'Save',
      success: 'Organization name updated successfully',
      errors: {
        required: 'Name is required',
        minLength: 'Name must be at least 1 character',
        maxLength: 'Name must be at most 256 characters',
      },
    },
  },
  changePassword: {
    modal: {
      title: 'Change password',
    },
    fields: {
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
    },
    passwordVisibility: {
      showCurrent: 'Show current password',
      hideCurrent: 'Hide current password',
      showNew: 'Show new password',
      hideNew: 'Hide new password',
      showConfirmation: 'Show new password confirmation',
      hideConfirmation: 'Hide new password confirmation',
    },
    errors: {
      currentPassword: {
        required: 'Current password is required',
        invalid: 'Current password is incorrect',
      },
      newPassword: {
        required: 'New password is required',
        minLength: 'Password must be at least 8 characters',
      },
      confirmPassword: {
        required: 'Password confirmation is required',
        mismatch: 'Passwords do not match',
      },
    },
    success: 'Password changed successfully',
    submit: 'Save',
  },
};

export default settingsTranslateEN;
